import Env from '@ridango/core/env';
import {
    AddressResult,
    SearchResult,
    StopRouteResult,
    TransitNetworkPartType,
    PoiAddressResult,
    PoiResult
} from './search.model';

export class SearchUtil {
    static isStopRouteResult(item: StopRouteResult | AddressResult | PoiAddressResult): item is StopRouteResult {
        return item.type === TransitNetworkPartType.Stop || item.type === TransitNetworkPartType.Route;
    }

    static isAddressResult(item: StopRouteResult | AddressResult | PoiAddressResult): item is AddressResult {
        return item.type === TransitNetworkPartType.Location;
    }

    static isPoiAddressResult(item: StopRouteResult | AddressResult | PoiAddressResult): item is PoiAddressResult {
        return item.type === TransitNetworkPartType.POI;
    }

    static isPOIResult(item: StopRouteResult | AddressResult | PoiResult): item is PoiResult {
        return item.type === TransitNetworkPartType.POI;
    }

    static startsWithNumber(searchPhrase: string, env?: Env): boolean {
        if ((env && !env.SEARCH_SETTINGS?.SEARCH_PARAMETERS?.includes('STOP_ROUTES')) || !searchPhrase) {
            return false;
        }
        return !Number.isNaN(Number(searchPhrase.charAt(0)));
    }

    //For routes (shortName): exact matches first, then starts with term, then alphabetically.
    //For stops (name): starts with term first, then alphabetically.
    static sortByRelevance<T>(
        items: T[],
        searchTerm: string,
        fieldName: keyof T,
        checkExactMatch: boolean = false
    ): T[] {
        const term = searchTerm.toLowerCase();

        return [...items].sort((a, b) => {
            const aValue = String(a[fieldName]).toLowerCase();
            const bValue = String(b[fieldName]).toLowerCase();

            if (checkExactMatch) {
                const aExact = aValue === term;
                const bExact = bValue === term;
                if (aExact && !bExact) return -1;
                if (!aExact && bExact) return 1;
            }

            const aStarts = aValue.startsWith(term);
            const bStarts = bValue.startsWith(term);
            if (aStarts && !bStarts) return -1;
            if (!aStarts && bStarts) return 1;

            return aValue.localeCompare(bValue);
        });
    }

    static limitSearchResults(results: SearchResult, env: Env): SearchResult {
        const resultsLimit = this.getSearchResultsLimit(env);

        return {
            stopRoutes: results.stopRoutes?.slice(0, resultsLimit) ?? [],
            addresses: results.addresses?.slice(0, resultsLimit) ?? [],
            poi: results.poi?.slice(0, resultsLimit) ?? []
        };
    }

    static getSearchResultsLimit(env: Env): number {
        switch (env.SEARCH_SETTINGS?.SEARCH_PARAMETERS?.length) {
            case 1:
                return 10;
            case 2:
                return 5;
            default:
                return 3;
        }
    }

    static sortArraysByRecentSearchItems(items, recentSearchItems, searchPhrase) {
        const { stopRoutes, addresses, poi } = items;
        if (this.startsWithNumber(searchPhrase)) {
            return {
                stopRoutes: this.uniqueRecent(stopRoutes),
                addresses: this.uniqueRecent(addresses),
                poi: this.uniqueRecent(poi)
            };
        }

        const matchedRecent =
            recentSearchItems?.filter((item) => {
                let targetPhrase = item.name;
                if (SearchUtil.isStopRouteResult(item) && item.shortName) {
                    targetPhrase += ' ' + item.shortName;
                }
                if (SearchUtil.isStopRouteResult(item) && item.code) {
                    targetPhrase += ' ' + item.code;
                }
                if (SearchUtil.isAddressResult(item) && item.name) {
                    targetPhrase += ' ' + item.name;
                }
                return targetPhrase?.toLowerCase().includes(searchPhrase ?? '');
            }) ?? [];

        const isRecentSearchItem = (item: any): number => {
            return recentSearchItems.filter((searchItem) => searchItem.id === item.id).length;
        };

        const sortedStopRoutes = this.uniqueRecent([
            ...matchedRecent.filter((r) => r.type === TransitNetworkPartType.Stop),
            ...stopRoutes
        ]).sort((a, b) => {
            return isRecentSearchItem(b) - isRecentSearchItem(a);
        });

        const sortedAddresses = this.uniqueRecent([
            ...matchedRecent.filter((r) => r.type === TransitNetworkPartType.Location),
            ...addresses
        ]).sort((a, b) => {
            return isRecentSearchItem(b) - isRecentSearchItem(a);
        });

        const sortedPoi = this.uniqueRecent([
            ...matchedRecent.filter((r) => r.type === TransitNetworkPartType.POI),
            ...poi
        ]).sort((a, b) => {
            return isRecentSearchItem(b) - isRecentSearchItem(a);
        });

        return {
            stopRoutes: sortedStopRoutes,
            addresses: sortedAddresses,
            poi: sortedPoi
        };
    }

    static uniqueRecent(array) {
        return array.reduce((accumulator, current) => {
            const isDuplicate = accumulator.some((obj) => obj.id === current.id);

            if (!isDuplicate) {
                accumulator.push(current);
            }

            return accumulator;
        }, []);
    }
}
